import { createClient } from '@connectrpc/connect';
import { create } from '@bufbuild/protobuf';
import {
  ApplyPcfVisualCheckRequestSchema,
  ClearPcfVisualCheckRequestSchema,
  LocalPcfGrouping,
  PcfExportService,
  PcfVisualParameterSource,
} from './gen/pcf_export_service_pb';
import { getGrpcTransport } from './transport';
import { isUnimplementedError } from './pcfExportContext';
import type { GroupBy } from '../../types/pcfExport.types';

export interface ApplyPcfVisualCheckOptions {
  mode: GroupBy;
  parameterName?: string;
  parameterSource?: 'Element' | 'Type';
}

export interface PcfVisualCheckResult {
  success: boolean;
  error?: string;
  versionMismatch?: boolean;
  viewName?: string;
  groupCount?: number;
  filterCount?: number;
  coloredElementCount?: number;
  ungroupedCount?: number;
  removedFilterCount?: number;
}

export async function applyPcfVisualCheckGrpc(
  options: ApplyPcfVisualCheckOptions
): Promise<PcfVisualCheckResult> {
  const client = getClient();
  if (!client) {
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.applyPcfVisualCheck(create(ApplyPcfVisualCheckRequestSchema, {
      grouping: mapGrouping(options.mode),
      parameterName: options.parameterName?.trim() ?? '',
      parameterSource: options.parameterSource === 'Type'
        ? PcfVisualParameterSource.TYPE
        : PcfVisualParameterSource.ELEMENT,
    }));
    return {
      success: response.isSuccess,
      error: response.error || undefined,
      viewName: response.viewName || undefined,
      groupCount: response.groupCount,
      filterCount: response.filterCount,
      coloredElementCount: response.coloredElementCount,
      ungroupedCount: response.ungroupedCount,
    };
  } catch (error) {
    return mapError(error, 'Visual check failed.');
  }
}

export async function clearPcfVisualCheckGrpc(): Promise<PcfVisualCheckResult> {
  const client = getClient();
  if (!client) {
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.clearPcfVisualCheck(create(ClearPcfVisualCheckRequestSchema));
    return {
      success: response.isSuccess,
      error: response.error || undefined,
      viewName: response.viewName || undefined,
      removedFilterCount: response.removedFilterCount,
    };
  } catch (error) {
    return mapError(error, 'Reset visual check failed.');
  }
}

function getClient() {
  const transport = getGrpcTransport();
  return transport ? createClient(PcfExportService, transport) : null;
}

function mapGrouping(mode: GroupBy): LocalPcfGrouping {
  if (mode === 'Parameter') {
    return LocalPcfGrouping.BY_PARAMETER;
  }
  return mode === 'SystemTypeName'
    ? LocalPcfGrouping.BY_SYSTEM_TYPE_NAME
    : LocalPcfGrouping.BY_SYSTEM_NAME;
}

function mapError(error: unknown, fallback: string): PcfVisualCheckResult {
  if (isUnimplementedError(error)) {
    return {
      success: false,
      versionMismatch: true,
      error: 'Update the PipeEX Revit plugin to use Visual check.',
    };
  }
  return {
    success: false,
    error: error instanceof Error ? error.message : fallback,
  };
}
